import { DecimalPipe } from "@angular/common";
import { Inject, Injectable, LOCALE_ID } from "@angular/core";

const SUFFIXES: { [key: number]: string } = {
  3: "k",
  6: "M",
  9: "B",
  12: "T"
};

@Injectable()
export class ShortNumberService {
  private underlyingPipe: DecimalPipe;

  constructor(@Inject(LOCALE_ID) _locale: string) {
    this.underlyingPipe = new DecimalPipe(_locale);
  }

  transform(value: any, digits?: string): string | null {
    let underlyingDigits: string | undefined = getUnderlyingDigits(digits);

    // within +/- 1k, don't need to use short number logic
    if (value < 1e3 && value > -1e3) {
      return trimExtraneousZeroes(
        this.underlyingPipe.transform(value, underlyingDigits),
        underlyingDigits
      );
    }

    // get the number of digits needed to represent value (exponent in base 10 + 1)
    const totalDigits: number = Math.ceil(Math.log10(Math.abs(value + 1)));

    // get the number of digits for the significand (called integer in underlying)
    const integerDigits: number = getIntegerDigits(digits);

    if (integerDigits >= totalDigits) {
      return trimExtraneousZeroes(
        this.underlyingPipe.transform(value, underlyingDigits),
        underlyingDigits
      );
    }

    const suffixExponent: number = Math.min(
      Math.ceil((totalDigits - integerDigits) / 3) * 3,
      12
    );
    const suffix: string = SUFFIXES[suffixExponent];

    value /= Math.pow(10, suffixExponent);
    return (
      trimExtraneousZeroes(
        this.underlyingPipe.transform(value, underlyingDigits),
        underlyingDigits
      ) + suffix
    );
  }
}

const DECIMAL_PIPE = new DecimalPipe("en-US");
export function shortNumTransform(value: any, digits?: string): string | null {
  let underlyingDigits: string | undefined = getUnderlyingDigits(digits);

  // within +/- 1k, don't need to use short number logic
  if (value < 1e3 && value > -1e3) {
    return trimExtraneousZeroes(
      DECIMAL_PIPE.transform(value, underlyingDigits),
      underlyingDigits
    );
  }

  // get the number of digits needed to represent value (exponent in base 10 + 1)
  const totalDigits: number = Math.ceil(Math.log10(Math.abs(value + 1)));

  // get the number of digits for the significand (called integer in underlying)
  const integerDigits: number = getIntegerDigits(digits);

  if (integerDigits >= totalDigits) {
    return trimExtraneousZeroes(
      DECIMAL_PIPE.transform(value, underlyingDigits),
      underlyingDigits
    );
  }

  const suffixExponent: number = Math.min(
    Math.ceil((totalDigits - integerDigits) / 3) * 3,
    12
  );
  const suffix: string = SUFFIXES[suffixExponent];

  value /= Math.pow(10, suffixExponent);
  return (
    trimExtraneousZeroes(
      DECIMAL_PIPE.transform(value, underlyingDigits),
      underlyingDigits
    ) + suffix
  );
}

function getIntegerDigits(digits?: string): number {
  if (digits == null) {
    return 3;
  }
  digits = digits.split(".")[0];
  if (digits === "") {
    return 3;
  }
  if (digits.includes("-")) {
    digits = digits.split("-")[1];
  }
  return Number(digits);
}

function getUnderlyingDigits(digits?: string): string | undefined {
  if (digits == null) {
    return digits;
  }
  let pieces: string[] = digits.split(".");
  if (!pieces[0].includes("-")) {
    return digits;
  }
  return pieces[0].split("-")[0] + (pieces.length > 1 ? "." + pieces[1] : "");
}

function trimExtraneousZeroes(
  numStr: string | null,
  underlyingDigits?: string
): string | null {
  if (numStr == null) {
    return null;
  }
  if (underlyingDigits == null) {
    return numStr;
  }

  let [integerString, fractionString] = numStr.split(".");
  if (fractionString == null) {
    return numStr;
  }

  const minFractionLength = +underlyingDigits[
    underlyingDigits.indexOf(".") + 1
  ];
  if (
    fractionString
      .slice(minFractionLength)
      .split("")
      .every((char) => char === "0")
  ) {
    fractionString = fractionString.slice(0, minFractionLength);
  }
  return fractionString.length === 0
    ? integerString
    : `${integerString}.${fractionString}`;
}
